import React, { useContext } from "react";

import {
  Button,
  Chip,
  Grid,
  TableCell,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Link } from "@mui/material";
import { Clipboard } from "react-feather";
import { Link as RouterLink, useHistory } from "react-router-dom";
import mainStyles from "styles";

import { ChargifyState } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_payment_pb";
import { SubscriptionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { chargifyStateDisplayMapping } from "constants/chargify";
import { scvLink, subscriptionTypeNameDisplayMapping } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { findSubscriptionIndex } from "helpers/find-subscription-index";
import { convertTimestampToDate, formatDateTimeDisplay } from "helpers/time";
import { SearchTableBodyRowOrgSearchProps } from "types/search-display";

const SearchTableBodyRowOrgSearch = ({
  row,
  regionCode,
}: SearchTableBodyRowOrgSearchProps) => {
  const classes = mainStyles();
  const theme = useTheme();
  const history = useHistory();
  const manageOrgHref = `/organisation/${regionCode}/${row.id}`;
  const tableauHref = `${scvLink}${row.id}`;
  const { copyToClipboard } = useContext(SnackbarContext);

  const subscriptionTypeChip = () => {
    if (row.subscription.length) {
      const type = row.subscription[findSubscriptionIndex(row)].type;
      const chipClass =
        type == SubscriptionType.FREE_ORG
          ? classes.yellowSubmarine
          : classes.purpleRain;
      return (
        <Chip
          size="small"
          color="primary"
          sx={{ ...chipClass }}
          label={subscriptionTypeNameDisplayMapping[type]}
          clickable
          onClick={() => history.push(manageOrgHref)}
        />
      );
    }
  };

  const chargifyStateChipStyleMap = {
    [ChargifyState.UNSPECIFIED]: classes.neutral,
    [ChargifyState.ACTIVE]: classes.greenDay,
    [ChargifyState.TRIALING]: classes.blueMoon,
    [ChargifyState.PAST_DUE]: classes.yellowSubmarine,
    [ChargifyState.CANCELED]: classes.redWine,
  };

  const chargifySubscriptionStateChip = () => {
    if (
      row.subscription[0]?.type !== SubscriptionType.FREE_ORG &&
      row.subscription[0]?.chargify &&
      row.subscription[0].chargify?.state !== ChargifyState.UNSPECIFIED
    ) {
      return (
        <Chip
          size="small"
          color="primary"
          data-testid="subscription-chip"
          sx={{
            ...chargifyStateChipStyleMap[row.subscription[0].chargify?.state],
            ...classes.basicSpacing,
          }}
          label={
            chargifyStateDisplayMapping[row.subscription[0].chargify?.state]
          }
          clickable
          onClick={() => history.push(manageOrgHref)}
        />
      );
    }
  };

  const displayNumMembers = () => {
    if (row.members.length === 1) {
      return `${row.members.length} Member`;
    }

    return `${row.members.length} Members`;
  };

  return (
    <TableRow>
      <TableCell>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={9}>
            <h3>
              <Link
                component={RouterLink}
                to={manageOrgHref}
                sx={classes.removeLinkStyle}
              >
                {row.name}
              </Link>{" "}
              {subscriptionTypeChip()}
              {chargifySubscriptionStateChip()}
            </h3>
            <div onClick={() => copyToClipboard(row.id)}>
              <Typography sx={classes.clickable} variant="body1" gutterBottom>
                {row.id}{" "}
                <Clipboard
                  color={theme.palette.text.primary}
                  size={"1rem"}
                  style={{ position: "relative", top: 2 }}
                />
              </Typography>
            </div>
            <div>
              <Typography variant="body2">{displayNumMembers()}</Typography>
            </div>

            <Typography variant="body1" paddingTop="5px">
              <b>Date registered: </b>
              {row.createdAt &&
                formatDateTimeDisplay(
                  convertTimestampToDate(row.createdAt)
                )}{" "}
            </Typography>
          </Grid>
          <Grid
            item
            xs={6}
            sm={3}
            container
            spacing={2}
            alignItems="flex-end"
            direction="column"
            style={{ marginBottom: 10, marginTop: 15 }}
          >
            <Grid item>
              <Link
                component={RouterLink}
                to={manageOrgHref}
                sx={classes.removeLinkStyle}
              >
                <Button
                  variant="contained"
                  color="primary"
                  data-anchor="org-manage-btn"
                >
                  Manage Organization
                </Button>
              </Link>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                href={tableauHref}
                target="_blank"
                color="primary"
                data-testid="org-tableau-scv-btn"
              >
                Tableau SCV
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </TableCell>
    </TableRow>
  );
};

export default SearchTableBodyRowOrgSearch;
